import config from "../config";

// A number can be stored in any of these formats (a batch may be mixed): three
// still-image formats or a short looping video. The frontend tries them in
// order; the edge gate serves whichever exists and 403s the rest, so a
// wrong-format request just falls through to the next candidate.
export const IMAGE_FORMATS = ["jpg", "jpeg", "png"];
export const VIDEO_FORMATS = ["mp4"];

const ImageModel = {
  // CDN mode (Phase 5): images are public-but-gated behind CloudFront, so a
  // URL is just constructed — no network call, no expiry. `ext` picks the format
  // (default jpg); the ?retry= buster is for self-heal retries after a transient
  // edge error.
  getImageUrl: (imageKey, forceRefresh = false, ext = "jpg") => {
    let url = `${config.imageBaseUrl}/images/${imageKey}.${ext}`;
    if (forceRefresh) {
      url += `?retry=${Date.now()}`;
    }
    return url;
  },

  // Every candidate URL for a number, jpg → jpeg → png, for format fallback.
  imageCandidates: (imageKey, forceRefresh = false) =>
    IMAGE_FORMATS.map((ext) => ImageModel.getImageUrl(imageKey, forceRefresh, ext)),

  // Typed candidates: still images first, then video. A consumer renders an
  // <img> for `image` and an autoplay-loop <video> for `video`, trying each in
  // order until one loads.
  mediaCandidates: (imageKey, forceRefresh = false) => [
    ...IMAGE_FORMATS.map((ext) => ({
      type: "image",
      ext,
      url: ImageModel.getImageUrl(imageKey, forceRefresh, ext),
    })),
    ...VIDEO_FORMATS.map((ext) => ({
      type: "video",
      ext,
      url: ImageModel.getImageUrl(imageKey, forceRefresh, ext),
    })),
  ],

  // Legacy presign mode: used while REACT_APP_IMAGE_BASE_URL is unset.
  // Removed in Phase 6 together with the GenerateSignedURL Lambda.
  fetchSignedUrl: async (imageKey, forceRefresh = false) => {
    let apiUrl = `${config.signedUrlApi}?imageKey=images/${imageKey}.jpg`;

    // Cache-busting for self-healing retries: the timestamp guarantees the
    // request cannot be answered by any intermediate cache.
    if (forceRefresh) {
      apiUrl += `&retry=${Date.now()}`;
    }

    // Presigned URLs expire 60 seconds after the backend mints them, so the
    // response must never be served from the browser's HTTP cache — a cached
    // response is an expired URL. Short-lived reuse is handled by the
    // in-memory cache in ImageViewModel instead.
    const response = await fetch(apiUrl, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`Signed URL request failed with status ${response.status}`);
    }
    const { url } = await response.json();
    return url;
  },
};

export default ImageModel;
